import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MeetUpsAPI } from "../../api/Meet-ups";
import { MembershipAPI } from "../../api/membership";
import { addedMembersColumns } from "../constant";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import Delete from "../../assets/SVGs/Table/Delete";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { DataGrid, GridCloseIcon } from "@mui/x-data-grid";
import { CustomNoRowsOverlay } from "../helper";
import Loader from "../Loader";
import { useAppContext } from "../../context/AppContext";
import DeleteModal from "./DeleteModal";
import { sortByFullName } from "../helper";

const EditMeetUpModal = ({
  open,
  handleClose,
  setIsLoading,
  id,
  title = "",
}) => {
  const navigate = useNavigate();
  const { openSnack } = useAppContext();
  const [activeMembers, setActiveMembers] = useState([]);
  const [loadingModal, setLoadingModal] = useState(true);
  const [initialFormState, setInitialFormState] = useState(null);
  const [formState, setFormState] = useState({
    meetup_date: "",
    added_active_members_ids: [],
  });
  const [formError, setFormError] = useState({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const fetchWeeklyMeet = async () => {
    if (id) {
      try {
        const res = await MeetUpsAPI.getMeet(id);
        const fetchedState = {
          ...res,
          added_active_members_ids: res?.added_active_members || [],
        };
        setFormState(fetchedState);
        setInitialFormState(fetchedState); // Save initial state
      } catch (e) {
        console.error("Error during fetching report", e);
      } finally {
        setLoadingModal(false);
      }
    }
  };

  const handleError = (formObj = formState) => {
    const temp = { ...formError };

    if ("meetup_date" in formObj) {
      temp.meetup_date = formObj.meetup_date ? "" : "Meet-Up Date is required.";
    }

    if ("added_active_members_ids" in formObj) {
      temp.added_active_members_ids =
        Array.isArray(formObj.added_active_members_ids) &&
        formObj.added_active_members_ids.length > 1
          ? ""
          : "Please add at least two active members.";
    }

    setFormError({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const isFormChanged = () => {
    return JSON.stringify(formState) !== JSON.stringify(initialFormState);
  };

  const handleChange = (key, value) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    handleError({ [key]: value });
  };

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("active");
      setActiveMembers(response?.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchWeeklyMeet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRemoveMember = (idToRemove) => {
    const updatedIds = formState.added_active_members_ids.filter(
      (activeMember) => activeMember?.id !== idToRemove,
    );
    handleChange("added_active_members_ids", updatedIds);
  };

  const columns = [
    ...addedMembersColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() => handleRemoveMember(params.row.id)}
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  const renderForm = () => {
    return (
      <Grid
        container
        spacing={2}
        rowSpacing={2.5}
        sx={{
          padding: "16px 0",
        }}
      >
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Meet-Up Date*"
            value={dayjs(formState?.meetup_date)}
            onChange={(newValue) => handleChange("meetup_date", newValue)}
            className="login-textField date-textField"
            sx={{ width: "100%", height: "40px" }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "meetup_date",
                name: "meetup_date",
                className: "login-textField",
                size: "small",
                error: !!formError.meetup_date,
                helperText: formError.meetup_date,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            limitTags={3}
            value={formState?.added_active_members_ids || []}
            onChange={(_e, newValue) =>
              handleChange("added_active_members_ids", newValue)
            }
            id="multiple-limit-tags"
            options={sortByFullName(activeMembers)}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`
            }
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                style={{
                  backgroundColor: selected && "#EAF2FF",
                  fontSize: "14px",
                  lineHeight: "20px",
                }}
              >
                {`${option.first_name} ${option.last_name}`}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add Active Members*"
                className="login-textField"
                size="small"
                error={!!formError.added_active_members_ids}
                helperText={formError.added_active_members_ids}
              />
            )}
            renderTags={(value, getTagProps) => {
              const numTags = value.length;
              const limitTags = 3;

              return (
                <>
                  {value.slice(0, limitTags).map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={`${option.first_name} ${option.last_name}`}
                      sx={{
                        backgroundColor: "#EAF2FF", // Change the chip background color
                        color: "#0F172A", // Change the chip text color
                        fontSize: "12px",
                        lineHeight: "18px",
                        height: "24px",
                      }}
                    />
                  ))}

                  {numTags > limitTags && ` +${numTags - limitTags}`}
                </>
              );
            }}
            className="login-textField"
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <h5 className="sub-header-text">Added Members</h5>
          <Stack
            sx={{
              width: "100%",
              height: "auto",
              overflow: "hidden",
              "& .MuiDataGrid-root": {
                borderRadius: "8px",
              },
              marginTop: "16px",
            }}
            className="customDataGridFigma"
          >
            <DataGrid
              rows={formState?.added_active_members_ids || []}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              checkboxSelection={false}
              rowSelection={false}
              rowHeight={48}
              columnHeaderHeight={40}
              getRowClassName={(params) => {
                const customClassName =
                  params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
                return ` ${customClassName}`.trim();
              }}
              slots={{
                noRowsOverlay: () =>
                  CustomNoRowsOverlay(
                    "No Members Added Yet",
                    "Your member list is currently empty. Add members to start collaborating.",
                  ),
              }}
              disableVirtualization
            />
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const editWeeklyMeet = async (form) => {
    const formObj = {
      meetup_date: dayjs(form.meetup_date).format("YYYY-MM-DD"),
      added_active_members_ids: form.added_active_members_ids.map(
        (member) => member.id,
      ),
    };
    try {
      await MeetUpsAPI.updateMeet(formObj, id);
      openSnack("Meet-Up information updated successfully.", "success");
      setIsLoading(true);
      handleClose();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  const handleSubmit = () => {
    if (handleError()) {
      editWeeklyMeet(formState);
    }
  };

  const handleDelete = async () => {
    try {
      await MeetUpsAPI.deleteMeet(id);
      openSnack("Meet-Up information deleted successfully.", "success");
      setIsLoading(true);
      setOpenDeleteModal(false);
      handleClose();
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) =>
        reason === "backdropClick" ? "" : handleClose()
      }
      hideBackdrop
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="custom-modal"
      sx={{
        ".MuiPaper-root": {
          width: "860px",
        },
      }}
    >
      {loadingModal ? (
        <Loader />
      ) : (
        <>
          <div className="modal-header">
            <h2 className="header-text">{title}</h2>
            <IconButton onClick={() => handleClose()}>
              <GridCloseIcon />
            </IconButton>
          </div>
          {renderForm()}

          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            className="modal-delete-btn-container"
          >
            <IconButton
              sx={{ flex: "0 0 auto", border: "1px solid #AEBBCB" }}
              onClick={() => {
                setOpenDeleteModal(true);
              }}
            >
              <Delete color="#D32F2F" />
            </IconButton>
            <div
              style={{ display: "flex", gap: "8px" }}
              className="modal-btn-container"
            >
              <Button
                onClick={handleClose}
                className="button-common light-blue-bg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="add-new-button button-common"
                disabled={!isFormChanged()}
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "#CBD5E1 !important",
                  },
                }}
              >
                Submit
              </Button>
            </div>
          </DialogActions>
        </>
      )}

      {openDeleteModal && (
        <DeleteModal
          id={id}
          open={openDeleteModal}
          handleClose={() => setOpenDeleteModal(false)}
          title="Meet-Up Deletion"
          desc={`Are you sure you want to delete <span style=font-weight:600>${title}</span> meet-up? This action cannot be undone.`}
          handleSubmit={handleDelete}
        />
      )}
    </Dialog>
  );
};

export default EditMeetUpModal;
